// Sliding-window rate limiter for the JS engine.
//
// Port of core/src/rate_limiter.rs. The Rust core applies these limits on
// every platform where it runs; the JS engine — which is the production engine
// on React Native and the fallback on web — applied none, so a chatty
// integration (a scroll handler, a render loop, a retry storm) could push
// unbounded events into the queue and out to ingest from exactly the platforms
// with the least supervision.
//
// The limits are the Rust defaults, hardcoded, because that is what the Rust
// core actually runs: `RateLimiter::new(None, None, None)` at client.rs:556,
// and `update_limits` is a documented no-op (rate_limiter.rs:109-122) that
// `update_remote_config` never calls. `events.rate_limits` from `/config` is
// parsed and discarded on every platform. Reading it here would make the JS
// engine the only one that obeys it — a divergence, not a fix.

/** Global cap, events per minute (rate_limiter.rs:8). */
const DEFAULT_GLOBAL_PER_MINUTE = 500;
/** Global cap, events per hour (rate_limiter.rs:11). */
const DEFAULT_GLOBAL_PER_HOUR = 10_000;
/** Per-event-name cap, events per minute (rate_limiter.rs:14). */
const DEFAULT_EVENT_PER_MINUTE = 100;

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

/**
 * Survey auto-events bypass the limiter (rate_limiter.rs:62-68): they are 1:1
 * with user interactions rather than a flood vector, and losing them corrupts
 * the server's frequency-cap accounting.
 */
const SURVEY_AUTO_EVENTS = new Set(['survey shown', 'survey dismissed', 'survey sent']);

/**
 * Above this many distinct event names, windows that have fully expired are
 * swept. Rust never collects its map, which is survivable for a process-scoped
 * native SDK; a React Native app that names events by id would leak for the
 * lifetime of the app. Dropping an EMPTY window is unobservable — an absent
 * window and a window that counts zero answer every question identically.
 */
const MAX_TRACKED_EVENT_NAMES = 500;

/**
 * Monotonic clock. Rust uses `Instant`, which never goes backwards; `Date.now()`
 * does, on an NTP correction or a user changing the device clock, and a
 * backwards jump would leave "future" timestamps in the window and over-limit
 * for up to an hour. `performance.now()` is monotonic where it exists (every
 * browser, Node, and Hermes on current React Native).
 */
function monotonicNow(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

/**
 * Counter over a continuously sliding window. Stores one timestamp per event
 * — no bucketing — exactly as `SlidingWindow` does in Rust
 * (rate_limiter.rs:125-156), and prunes only on read.
 */
class SlidingWindow {
  private timestamps: number[] = [];

  constructor(private readonly windowMs: number) {}

  /** Events still inside the window, pruning anything that fell out. */
  count(now: number): number {
    const cutoff = now - this.windowMs;
    // `>=`, inclusive: a timestamp exactly one window old still counts.
    if (this.timestamps.length > 0 && this.timestamps[0]! < cutoff) {
      this.timestamps = this.timestamps.filter((t) => t >= cutoff);
    }
    return this.timestamps.length;
  }

  record(now: number): void {
    this.timestamps.push(now);
  }
}

export interface RateLimiterOptions {
  globalPerMinute?: number;
  globalPerHour?: number;
  eventPerMinute?: number;
}

export class RateLimiter {
  private readonly globalPerMinute: number;
  private readonly globalPerHour: number;
  private readonly eventPerMinute: number;

  private readonly globalMinute = new SlidingWindow(MINUTE_MS);
  private readonly globalHour = new SlidingWindow(HOUR_MS);
  private readonly perEventMinute = new Map<string, SlidingWindow>();

  constructor(options: RateLimiterOptions = {}) {
    this.globalPerMinute = options.globalPerMinute ?? DEFAULT_GLOBAL_PER_MINUTE;
    this.globalPerHour = options.globalPerHour ?? DEFAULT_GLOBAL_PER_HOUR;
    this.eventPerMinute = options.eventPerMinute ?? DEFAULT_EVENT_PER_MINUTE;
  }

  /**
   * `true` when the event may proceed — and, when it may, the three windows
   * record it. Port of `check_and_record` (rate_limiter.rs:57-107).
   *
   * Check-then-record is all-or-nothing: every window is checked before any is
   * written, so a REJECTED event consumes no budget and cannot starve the next
   * one. Comparisons are `>=`, so a limit of N admits exactly N events.
   */
  checkAndRecord(eventName: string): boolean {
    // System events bypass every limit — `$first_open`, `$feature_flag_called`
    // and friends are SDK-generated and bounded by construction.
    if (eventName.startsWith('$')) return true;
    if (SURVEY_AUTO_EVENTS.has(eventName)) return true;

    const now = monotonicNow();

    if (this.globalMinute.count(now) >= this.globalPerMinute) return false;
    if (this.globalHour.count(now) >= this.globalPerHour) return false;

    let eventWindow = this.perEventMinute.get(eventName);
    if (!eventWindow) {
      eventWindow = new SlidingWindow(MINUTE_MS);
      this.perEventMinute.set(eventName, eventWindow);
    }
    if (eventWindow.count(now) >= this.eventPerMinute) return false;

    this.globalMinute.record(now);
    this.globalHour.record(now);
    eventWindow.record(now);

    if (this.perEventMinute.size > MAX_TRACKED_EVENT_NAMES) {
      this.sweepExpired(now);
    }
    return true;
  }

  /** Drop per-event windows that no longer hold anything. */
  private sweepExpired(now: number): void {
    for (const [name, window] of this.perEventMinute) {
      if (window.count(now) === 0) this.perEventMinute.delete(name);
    }
  }
}
